import {
  createAuthService,
  LocalStorageTokenStore,
  type AuthProviderToken,
} from "@libertify/auth/core";
import { API_URL } from "@/constants";
import { STORAGE_KEY_PREFIX as MFA_SETUP_DISMISSED_PREFIX } from "@/utils/mfaSetupDismissed";
import { COOKIE_CONSENT_KEY } from "@libertify/ui";

/**
 * Admin's AuthService instance. The lib owns:
 *   - Token storage (via LocalStorageTokenStore; same `refine-auth` key)
 *   - Refresh queue + per-provider refresh dispatch
 *   - Logout cleanup (storage wipe + per-provider logout handler)
 *
 * The lib's `createAuthProvider` (wired in `./authProvider.ts`) owns the
 * Refine-side flow including the Scalekit SLO redirect on logout — DO NOT
 * re-wrap `logout` here to return a different shape, or the lib's SLO branch
 * (which destructures `{ token }` from `service.logout()`) goes dead.
 */
export const authService = createAuthService({
  apiUrl: API_URL,
  storage: new LocalStorageTokenStore({
    // Logout wipes all of localStorage — keep the "2FA nudge dismissed"
    // flag so it doesn't reappear on the next login, and the cookie consent
    // answer, which belongs to the browser rather than the account.
    preserveKeyPrefixes: [`${MFA_SETUP_DISMISSED_PREFIX}:`, COOKIE_CONSENT_KEY],
  }),
  refreshHandlers: {
    google: async (token: AuthProviderToken) => {
      const { refreshOAuthSession, getOAuthSessionAccessToken } = await import(
        "./oauth"
      );
      const session = await refreshOAuthSession();
      const newAccessToken = getOAuthSessionAccessToken(session);
      return newAccessToken
        ? { ...token, access_token: newAccessToken }
        : null;
    },
  },
  logoutHandlers: {
    google: async () => {
      const { signOutFromOAuth } = await import("./oauth");
      await signOutFromOAuth();
    },
  },
});

export type { AuthStateSubscriber, MfaSession } from "@libertify/auth/core";
